import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLanguage } from '../../../../../hooks/useLanguage';
import { products as frProducts } from '../../../../../locales/fr/products';
import { products as esProducts } from '../../../../../locales/es/products';
import { products as arProducts } from '../../../../../locales/ar/products';
import useFormNavigation from '../../../../../hooks/useFormNavigation';
import { ProductService } from '../../../../../services/AppDataServices/services/product.service';
import useSearch from '../../../hooks/useSearch';
import useImageOptimizer from '../../../../../hooks/useImageOptimizer';
import FloatingAlert from '../../../../../components/Common/FloatingAlert';

const translations = {
  fr: frProducts,
  es: esProducts,
  ar: arProducts
};

const ProductForm = ({ product, isEditMode, categories, products, onSubmit, onCancel, isSubmitting, alert, onAlertClose, onResetToCreate }) => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { handleFormKeyDown } = useFormNavigation();
  const productService = new ProductService();

  // Hook d'optimisation d'image avec des paramètres adaptés aux produits
  const { optimizeImage, isOptimizing: isImageOptimizing, error: imageError } = useImageOptimizer({
    maxInitialSize: 5, // 5MB max initial
    maxFinalSize: 0.5, // 500KB max final pour IndexedDB
    maxWidth: 800,
    maxHeight: 800,
    initialQuality: 0.8,
    qualityStep: 0.1,
    minQuality: 0.3
  });

  const [formData, setFormData] = useState({
    barcode: '',
    name: '',
    description: '',
    categoryId: '',
    categoryName: '',
    price: 0,
    cost: 0,
    tauxTva: 20,
    stock: 0,
    Type: 'product',
    packItems: [],
    image: '',
    isActive: true,
    unite: 'piece'
  });

  const [errors, setErrors] = useState({});
  const [showPackItems, setShowPackItems] = useState(false);
  const [activeTab, setActiveTab] = useState('main'); // 'main' ou 'secondary'

  // État pour la recherche de produits
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Configuration de la recherche
  const searchConfig = useMemo(() => ({
    minMatchLength: 2,
    fields: [
      { key: 'name', type: 'text' },
      { key: 'barcode', type: 'text' },
      { key: 'description', type: 'text' }
    ]
  }), []);

  const { searchItems } = useSearch(searchConfig);

  // Options pour les types de produits
  const typeOptions = [
    { value: 'product', label: t.filters.type.product || 'Produit' },
    { value: 'service', label: t.filters.type.service || 'Service' },
    { value: 'pack', label: t.filters.type.pack || 'Pack/Composition' },
    { value: 'matiere_premiere', label: t.filters.type.matiere_premiere || 'Matière première' },
    { value: 'piece_detachee', label: t.filters.type.piece_detachee || 'Pièce détachée' }
  ];

  useEffect(() => {
    if (product) {
      setFormData({
        barcode: product.barcode || '',
        name: product.name || '',
        description: product.description || '',
        categoryId: product.categoryId?.toString() || '',
        categoryName: product.categoryName || '',
        price: product.price || 0,
        cost: product.cost || 0,
        tauxTva: product.tauxTva || 20,
        stock: product.stock || 0,
        Type: product.Type || 'product',
        packItems: product.packItems || [],
        image: product.image || '',
        isActive: product.isActive ?? true,
        unite: product.unite || 'piece'
      });
      setShowPackItems(product.Type === 'pack' || (product.packItems && product.packItems.length > 0));
    }
  }, [product]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t.form.validations.required;
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = t.form.validations.required;
    }

    if (formData.price < 0) {
      newErrors.price = t.form.validations.positive;
    }

    if (formData.cost < 0) {
      newErrors.cost = t.form.validations.positive;
    }

    if (formData.stock < 0) {
      newErrors.stock = t.form.validations.positive;
    }

    if (formData.tauxTva < 0 || formData.tauxTva > 100) {
      newErrors.tauxTva = 'La TVA doit être entre 0 et 100%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Trouver le nom de la catégorie sélectionnée
      const selectedCategory = categories.find(cat => cat.value.toString() === formData.categoryId);
      const categoryName = selectedCategory ? selectedCategory.label : '';
      
      // Préparer les données pour l'envoi
      const submitData = {
        ...formData,
        categoryId: parseInt(formData.categoryId),
        categoryName: categoryName,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost),
        tauxTva: parseFloat(formData.tauxTva),
        stock: parseInt(formData.stock)
      };
      onSubmit(submitData);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseFloat(value) || 0 : 
              value
    }));
    
    // Gérer l'affichage des packItems selon le type
    if (name === 'Type') {
      setShowPackItems(value === 'pack');
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Optimiser l'image avant de la stocker
        const optimizedImage = await optimizeImage(file);
        setFormData(prev => ({
          ...prev,
          image: optimizedImage
        }));
      } catch (error) {
        console.error('Erreur lors de l\'optimisation de l\'image:', error);
        // Optionnel : afficher une alerte à l'utilisateur
        alert(`Erreur lors de l'optimisation de l'image: ${error.message}`);
      }
    }
  };

  // Fonction de recherche de produits avec délai
  const searchProducts = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      
      // Récupérer tous les produits depuis la base de données
      let allProducts = await productService.getAll();
      
      // Exclure le produit actuel de la recherche si on est en mode édition
      if (product && product.id) {
        allProducts = allProducts.filter(p => p.id !== product.id);
      }

      // Appliquer la recherche avec useSearch
      const filteredProducts = searchItems(term, allProducts);

      // Tri par pertinence
      const sortedResults = filteredProducts.sort((a, b) => {
        const searchTermLower = term.toLowerCase();
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aBarcode = a.barcode || '';
        const bBarcode = b.barcode || '';
        
        // Score de pertinence
        const getScore = (item) => {
          let score = 0;
          if (item.name.toLowerCase() === searchTermLower) score += 100;
          if (item.barcode === term) score += 100;
          if (item.name.toLowerCase().startsWith(searchTermLower)) score += 50;
          if (item.barcode && item.barcode.startsWith(term)) score += 50;
          if (item.name.toLowerCase().includes(searchTermLower)) score += 10;
          if (item.barcode && item.barcode.includes(term)) score += 10;
          return score;
        };
        
        return getScore(b) - getScore(a);
      });

      setSearchResults(sortedResults);
    } catch (error) {
      console.error('Erreur lors de la recherche de produits:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [productService, searchItems, product]);

  // Fonction pour gérer la recherche avec délai
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    
    // Annuler le délai précédent
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Créer un nouveau délai
    const timeout = setTimeout(() => {
      searchProducts(term);
    }, 300); // 300ms de délai
    
    setSearchTimeout(timeout);
  }, [searchProducts, searchTimeout]);

  // Fonction pour sélectionner un produit depuis la recherche
  const selectProduct = (product) => {
    setFormData(prev => ({
      ...prev,
      packItems: [...prev.packItems, {
        productId: product.id.toString(),
        barcode: product.barcode,
        name: product.name,
        quantity: 1
      }]
    }));
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchModal(false);
  };

  // Fonction pour ajouter un élément manuellement
  const addPackItem = () => {
    setFormData(prev => ({
      ...prev,
      packItems: [...prev.packItems, {
        productId: '',
        barcode: '',
        name: '',
        quantity: 1
      }]
    }));
  };

  const removePackItem = (index) => {
    setFormData(prev => ({
      ...prev,
      packItems: prev.packItems.filter((_, i) => i !== index)
    }));
  };

  const updatePackItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      packItems: prev.packItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Exemple de structure packItems pour un pack "Kit de menuiserie basique"
  // const examplePackItems = [
  //   {
  //     productId: '1',
  //     barcode: '123456789',
  //     name: 'Planche de bois (2m)',
  //     quantity: 2
  //   },
  //   {
  //     productId: '2', 
  //     barcode: '987654321',
  //     name: 'Vis à bois (5cm)',
  //     quantity: 20
  //   },
  //   {
  //     productId: '3',
  //     barcode: '456789123',
  //     name: 'Colle à bois',
  //     quantity: 1
  //   },
  //   {
  //     productId: '4',
  //     barcode: '789123456',
  //     name: 'Papier de verre (grain 120)',
  //     quantity: 2
  //   }
  // ];

  return (
    <form 
      onSubmit={handleSubmit} 
      onKeyDown={handleFormKeyDown}
      className="space-y-4"
    >
      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            type="button"
            onClick={() => setActiveTab('main')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'main'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t.form.tabs.main}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('secondary')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'secondary'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t.form.tabs.secondary}
          </button>
        </nav>
      </div>

      {/* Onglet Informations principales */}
      {activeTab === 'main' && (
        <div className="space-y-4">
          {/* Informations de base */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="barcode" className="block text-sm font-medium text-gray-700">
                {t.form.fields.barcode}
              </label>
              <input
                type="text"
                id="barcode"
                name="barcode"
                value={formData.barcode}
                onChange={handleChange}
                placeholder={t.form.placeholders.barcode}
                className="input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                {t.form.fields.name} *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t.form.placeholders.name}
                className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
                  errors.name ? 'input-error' : ''
                }`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700">
                {t.form.fields.category} *
              </label>
              <select
                id="categoryId"
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
                className={`select select-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
                  errors.categoryId ? 'select-error' : ''
                }`}
              >
                <option value="">{t.form.placeholders.category}</option>
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
              {errors.categoryId && (
                <p className="mt-1 text-sm text-red-600">{errors.categoryId}</p>
              )}
            </div>

            <div>
              <label htmlFor="Type" className="block text-sm font-medium text-gray-700">
                Type de produit
              </label>
              <select
                id="Type"
                name="Type"
                value={formData.Type}
                onChange={handleChange}
                className="select select-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
              >
                {typeOptions.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="stock" className="block text-sm font-medium text-gray-700">
                {t.form.fields.stock}
              </label>
              <input
                type="number"
                id="stock"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                min="0"
                placeholder={t.form.placeholders.stock}
                className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
                  errors.stock ? 'input-error' : ''
                }`}
              />
              {errors.stock && (
                <p className="mt-1 text-sm text-red-600">{errors.stock}</p>
              )}
            </div>
          </div>

          {/* Informations financières */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                {t.form.fields.price} (Dh)
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder={t.form.placeholders.price}
                className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
                  errors.price ? 'input-error' : ''
                }`}
              />
              {errors.price && (
                <p className="mt-1 text-sm text-red-600">{errors.price}</p>
              )}
            </div>

            <div>
              <label htmlFor="cost" className="block text-sm font-medium text-gray-700">
                {t.form.fields.cost} (Dh)
              </label>
              <input
                type="number"
                id="cost"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="Entrez le coût"
                className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
                  errors.cost ? 'input-error' : ''
                }`}
              />
              {errors.cost && (
                <p className="mt-1 text-sm text-red-600">{errors.cost}</p>
              )}
            </div>

            <div>
              <label htmlFor="tauxTva" className="block text-sm font-medium text-gray-700">
                {t.form.fields.tauxTva} (%)
              </label>
              <input
                type="number"
                id="tauxTva"
                name="tauxTva"
                value={formData.tauxTva}
                onChange={handleChange}
                step="0.1"
                min="0"
                max="100"
                placeholder="20"
                className={`input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 ${
                  errors.tauxTva ? 'input-error' : ''
                }`}
              />
              {errors.tauxTva && (
                <p className="mt-1 text-sm text-red-600">{errors.tauxTva}</p>
              )}
            </div>
          </div>

          {/* Composition du pack */}
          {showPackItems && (
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Composition du pack</h3>
                <button
                  type="button"
                  onClick={() => setShowSearchModal(true)}
                  className="btn btn-sm btn-secondary"
                >
                  Rechercher un produit
                </button>
              </div>
              
              {formData.packItems.map((item, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
                  <div className="input input-bordered input-sm bg-gray-100 text-gray-700">
                    {item.name}
                  </div>
                  <input
                    type="number"
                    placeholder="Quantité"
                    value={item.quantity}
                    onChange={(e) => updatePackItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                    className="input input-bordered input-sm"
                    min="1"
                    step="1"
                  />
                  <button
                    type="button"
                    onClick={() => removePackItem(index)}
                    className="btn btn-sm btn-error"
                  >
                    Supprimer
                  </button>
                </div>
              ))}
              
              {formData.packItems.length === 0 && (
                <p className="text-gray-500 text-sm">Aucun élément dans la composition</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Onglet Informations secondaires */}
      {activeTab === 'secondary' && (
        <div className="space-y-4">
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Entrez la description du produit"
              rows={4}
              className="textarea textarea-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
          </div>

          {/* Unité de mesure */}
          <div>
            <label htmlFor="unite" className="block text-sm font-medium text-gray-700">
              Unité de mesure
            </label>
            <select
              id="unite"
              name="unite"
              value={formData.unite}
              onChange={handleChange}
              className="select select-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            >
              <option value="piece">{t.units.piece || 'Pièce'}</option>
              <option value="kg">{t.units.kg || 'Kilogramme'}</option>
              <option value="g">{t.units.g || 'Gramme'}</option>
              <option value="l">{t.units.l || 'Litre'}</option>
              <option value="ml">{t.units.ml || 'Millilitre'}</option>
              <option value="m">{t.units.m || 'Mètre'}</option>
              <option value="cm">{t.units.cm || 'Centimètre'}</option>
              <option value="m2">{t.units.m2 || 'Mètre carré'}</option>
              <option value="m3">{t.units.m3 || 'Mètre cube'}</option>
              <option value="h">{t.units.h || 'Heure'}</option>
              <option value="forfait">{t.units.forfait || 'Forfait'}</option>
            </select>
          </div>

          {/* Image du produit */}
          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700">
              {t.form.fields.image}
            </label>
            <div className="mt-2">
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleImageChange}
                accept="image/*"
                className="hidden"
                disabled={isImageOptimizing}
              />
              <div className="relative inline-block">
                <div 
                  className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-base-300 cursor-pointer hover:border-primary"
                  onClick={() => document.getElementById('image').click()}
                >
                  {formData.image ? (
                    <img 
                      src={formData.image} 
                      alt="Aperçu" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-base-200">
                      <svg className="w-12 h-12 text-base-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-xs text-base-300 mt-1">{t.form.imageHelp}</span>
                    </div>
                  )}
                  {formData.image && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, image: '' }));
                      }}
                      className="absolute top-1 right-1 btn btn-circle btn-xs btn-error"
                      title="Supprimer"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
            {isImageOptimizing && (
              <div className="mt-2 flex items-center text-sm text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Optimisation de l'image en cours...
              </div>
            )}
            {imageError && (
              <div className="mt-2 text-sm text-red-600">
                Erreur d'optimisation : {imageError}
              </div>
            )}
          </div>

          {/* Statut actif */}
          <div className="flex items-center justify-start">
            <input
              type="checkbox"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="checkbox checkbox-primary"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
              Actif
            </label>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex justify-between items-center pt-4">
        <div className="flex space-x-2">
          {isEditMode && (
            <button
              type="button"
              onClick={() => {
                // Réinitialiser le formulaire pour un nouveau produit
                setFormData({
                  barcode: '',
                  name: '',
                  description: '',
                  categoryId: '',
                  categoryName: '',
                  price: 0,
                  cost: 0,
                  tauxTva: 20,
                  stock: 0,
                  Type: 'product',
                  packItems: [],
                  image: '',
                  isActive: true,
                  unite: 'piece'
                });
                setErrors({});
                setActiveTab('main');
                setShowPackItems(false);
                
                // Masquer l'alert s'il y en a un
                if (onAlertClose) {
                  onAlertClose();
                }
                
                // Passer en mode création
                if (onResetToCreate) {
                  onResetToCreate();
                }
              }}
              className="btn btn-secondary btn-sm"
              disabled={isSubmitting}
            >
              <span className="text-lg">+</span>
              {/* <span className="ml-1">Nouveau produit</span> */}
            </button>
          )}
        </div>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-ghost"
          >
            {t.form.cancel}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary"
          >
            {isSubmitting ? t.form.submitting : (isEditMode ? t.form.update : t.form.create)}
          </button>
        </div>
      </div>

      {/* Modal de recherche de produits */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Rechercher un produit à ajouter</h3>
              <button
                type="button"
                onClick={() => {
                  setShowSearchModal(false);
                  setSearchTerm('');
                  setSearchResults([]);
                }}
                className="btn btn-sm btn-ghost"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <input
                type="text"
                placeholder="Rechercher par nom ou code-barres..."
                value={searchTerm}
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                className="input input-bordered w-full py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                autoFocus
              />
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {searchLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p className="text-gray-500">Recherche en cours...</p>
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map((product) => {
                  const searchTermLower = searchTerm.toLowerCase();
                  const nameLower = product.name.toLowerCase();
                  const barcode = product.barcode || '';
                  
                  // Mise en surbrillance des correspondances
                  const highlightText = (text, searchTerm) => {
                    if (!searchTerm || !text) return text;
                    const regex = new RegExp(`(${searchTerm})`, 'gi');
                    return text.replace(regex, '<mark class="bg-yellow-200 px-1 rounded">$1</mark>');
                  };
                  
                  return (
                    <div
                      key={product.id}
                      onClick={() => selectProduct(product)}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div 
                            className="font-medium text-gray-900"
                            dangerouslySetInnerHTML={{ 
                              __html: highlightText(product.name, searchTerm) 
                            }}
                          />
                          <div className="text-sm text-gray-500">
                            Code: <span dangerouslySetInnerHTML={{ 
                              __html: highlightText(barcode, searchTerm) 
                            }} />
                          </div>
                          {nameLower === searchTermLower && (
                            <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded mt-1">
                              Nom exact
                            </span>
                          )}
                          {barcode === searchTerm && (
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mt-1 ml-1">
                              Code exact
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-medium text-gray-900 ml-4">
                          {product.price ? `${product.price.toFixed(2)} Dh` : 'N/A'}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : searchTerm ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-2">Aucun produit trouvé pour "{searchTerm}"</p>
                  <p className="text-sm text-gray-400">Essayez avec un autre terme de recherche</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 mb-2">Commencez à taper pour rechercher</p>
                  <p className="text-sm text-gray-400">Recherchez par nom ou code-barres</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* FloatingAlert pour les messages de succès/erreur */}
      <FloatingAlert
        open={alert?.open || false}
        onClose={onAlertClose}
        type={alert?.type || 'info'}
      >
        {alert?.message || ''}
      </FloatingAlert>
    </form>
  );
};

export default ProductForm; 